<script>
import dayjs from 'dayjs';
import { mapMutations, mapActions, mapState } from 'vuex';
import LottieAnimation from 'lottie-web-vue';

import AppButton from '@/common/AppButton.vue';

import { handleNumberInput } from '@/utils';
import heartData from '@/assets/heart-lottie.json';

export default {
  name: 'FormOften',
  components: {
    AppButton,
    LottieAnimation
  },
  props: {
    active: Boolean,
    showError: Boolean,
    staticForm: Boolean,
    formCampaignSettings: {
      type: Object,
      default: () => null
    },
    localFrequency: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      dayjs,
      handleNumberInput,
      heartData,
      hideHeartSvg: false
    };
  },
  computed: {
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapState('publicOrgSettings', ['settings']),
    ...mapState('campaign', {
      campaignSettings: (campaign) => campaign.settings
    }),
    ...mapState('campaign', {
      campaignSettings: (campaign) => campaign.settings
    }),
    // Since this component is currently also used for the static form which relies on the default campaign settings
    settingsComputed() {
      if (this.formCampaignSettings) {
        return this.formCampaignSettings;
      }
      return this.staticForm ? this.settings : this.campaignSettings;
    },
    frequencies() {
      return [
        { value: 'single', label: this.$t('donationFrequency.oneTime') },
        { value: 'daily', label: this.$t('donationFrequency.daily') },
        { value: 'weekly', label: this.$t('donationFrequency.weekly') },
        { value: 'monthly', label: this.$t('donationFrequency.monthly') },
        { value: 'yearly', label: this.$t('donationFrequency.yearly') }
      ];
    },
    frequenciesEnabled() {
      return this.frequencies.filter((frequency) => this.settingsComputed.frequenciesEnabled?.includes(frequency.value));
    },
    frequenciesRecurringEnabled() {
      return this.frequenciesEnabled.filter((frequency) => frequency.value !== 'single');
    },
    isCurrentFrequencyEnabled() {
      return this.frequenciesEnabled.map((f) => f.value).includes(this.frequency);
    },

    numCharges: {
      get() {
        return this.donation.numCharges;
      },
      set(value) {
        this.SET_NUM_CHARGES(value);
      }
    },

    frequency: {
      get() {
        return this.staticForm ? this.localFrequency : this.donation.frequency;
      },
      set(value) {
        if (this.staticForm) {
          this.$emit('update:local-frequency', value);
        } else {
          this.SET_FREQUENCY_ACTIONS(value);
        }
      }
    },

    minDate() {
      const momentStrings = {
        weekly: 'weeks',
        monthly: 'months',
        yearly: 'years'
      };
      if (this.frequency !== 'single') {
        return dayjs().startOf('day').add(1, momentStrings[this.frequency]);
      }
      return dayjs();
    },
    showToggle() {
      // Hide toggle if 3 recurring are enabled
      if (this.frequenciesEnabled.length === 4) {
        return true;
      }
      return this.frequenciesRecurringEnabled.length !== 3;
    }
  },
  methods: {
    ...mapMutations('donation', ['SET_NUM_CHARGES']),
    ...mapActions('donation', ['SET_FREQUENCY_ACTIONS']),
    ...mapMutations('validation', ['SET_IS_RECURRING_END_VALID']),
    selectFrequency(value) {
      this.frequency = value;
    },

    complete() {
      this.hideHeartSvg = false;
    },
    showHeart(frequency) {
      if (this.staticForm && window.innerWidth < 375) {
        return false;
      }
      return frequency === this.settingsComputed.frequencyPromoted;
    }
  }
};
</script>

<template>
  <section
    ref="form-often"
    :class="{
      'section-has-error': showError && !frequency
    }"
    class="form-often mb-8"
  >
    <div v-if="showToggle" class="often-buttons buttons relative flex bg-off-white">
      <template v-if="frequenciesEnabled.length === 2">
        <app-button
          v-for="(freqOption, i) in frequenciesEnabled"
          variant="ghost"
          :selected="frequency === freqOption.value"
          :key="i"
          class="flex-1"
          @click="selectFrequency(freqOption.value)"
        >
          <h3 class="bold text-center flex items-center justify-center">
            <span v-if="showHeart(freqOption.value)" class="heart-container">
              <lottie-animation v-if="frequency === freqOption.value" class="lottie" :animation-data="heartData" :loop="0.5" />
              <svg v-else class="heart" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path
                  d="M256,448a32,32,0,0,1-18-5.57c-78.59-53.35-112.62-89.93-131.39-112.8-40-48.75-59.15-98.8-58.61-153C48.63,114.52,98.46,64,159.08,64c44.08,0,74.61,24.83,92.39,45.51a6,6,0,0,0,9.06,0C278.31,88.81,308.84,64,352.92,64,413.54,64,463.37,114.52,464,176.64c.54,54.21-18.63,104.26-58.61,153-18.77,22.87-52.8,59.45-131.39,112.8A32,32,0,0,1,256,448Z"
                />
              </svg>
            </span>
            {{ freqOption.label }}
          </h3>
        </app-button>
      </template>
      <template v-else-if="frequenciesEnabled.length > 2">
        <app-button variant="ghost" :selected="frequency === frequencies[0].value" class="flex-1" @click="selectFrequency(frequencies[0].value)">
          <h3 class="bold text-center">{{ frequencies[0].label }}</h3>
        </app-button>
        <app-button variant="ghost" :selected="frequency !== 'single'" class="flex-1" @click="selectFrequency(frequenciesRecurringEnabled[0].value)">
          <h3 class="bold text-center flex items-center justify-center">
            <span class="heart-container">
              <lottie-animation v-if="frequency !== 'single'" class="lottie" :animation-data="heartData" :loop="0.5" />
              <svg v-else class="heart" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path
                  d="M256,448a32,32,0,0,1-18-5.57c-78.59-53.35-112.62-89.93-131.39-112.8-40-48.75-59.15-98.8-58.61-153C48.63,114.52,98.46,64,159.08,64c44.08,0,74.61,24.83,92.39,45.51a6,6,0,0,0,9.06,0C278.31,88.81,308.84,64,352.92,64,413.54,64,463.37,114.52,464,176.64c.54,54.21-18.63,104.26-58.61,153-18.77,22.87-52.8,59.45-131.39,112.8A32,32,0,0,1,256,448Z"
                />
              </svg>
            </span>
            {{ $t('donationFrequency.recurring') }}
          </h3>
        </app-button>
      </template>
    </div>

    <transition name="expand">
      <div v-if="frequenciesEnabled.length > 2 && frequency !== 'single'" class="mt-4.5">
        <h4>{{ $t('donationFrequency.title') }}</h4>
        <app-select
          :value="frequenciesEnabled.find((f) => f.value === frequency)?.label"
          @input="selectFrequency($event.value)"
          label="label"
          :options="frequenciesEnabled.filter((f) => f.value !== 'single')"
          :searchable="false"
          name="frequency"
          id="frequency"
          placeholder="Select a frequency"
          class="flex-1 my-2"
        />
      </div>
    </transition>

    <span v-if="showError && !frequency" class="custom-error-message">{{ $t('donationFrequency.errorMessage') }}</span>
  </section>
</template>
